import HealingRoundedIcon from "@mui/icons-material/HealingRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { Box, Button, Chip, Divider, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { CONNECTION_TYPE_META } from "@/features/network-topology/constants";
import type { ConnectionType } from "@/features/network-topology/types";
import { useNetworkActions, useNetworkTopology } from "@/features/network-topology/useNetworkTopology";

/**
 * 選択中のリンク (親 → 子) の詳細＋操作パネル。
 *
 * 親 / 子 / 接続方式 を表示し、切断・接続、接続方式の変更が可能。
 * 子ノード id でリンクを識別する（ツリー構造で 1:1）。
 */
export function EdgeDetailPanel() {
  const { nodes, selectedEdgeChildId } = useNetworkTopology();
  const { disconnectLink, startConnectLink, setLinkType } = useNetworkActions();

  const child = selectedEdgeChildId ? nodes.find((n) => n.id === selectedEdgeChildId) : null;
  const parent = child?.parentId ? nodes.find((n) => n.id === child.parentId) : null;

  if (!child || !parent) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          リンクを選択してください
        </Typography>
      </Box>
    );
  }

  const linkType: ConnectionType = child.linkType ?? "rj45";
  const meta = CONNECTION_TYPE_META[linkType];
  const isDisconnected = child.linkDisconnected === true;

  return (
    <Stack sx={{ p: 2, gap: 1.5, height: "100%", overflow: "auto" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Link
      </Typography>
      <Stack spacing={0.5} sx={{ p: 1, background: "rgba(0,0,0,0.03)", borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          From
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {parent.label}{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            ({parent.kind})
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          To
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {child.label}{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            ({child.kind})
          </Typography>
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Chip size="small" label={meta.label} sx={{ fontWeight: 700, color: "#fff", background: meta.color }} />
        <Chip
          size="small"
          color={isDisconnected ? "error" : "success"}
          label={isDisconnected ? "DISCONNECTED" : "CONNECTED"}
          sx={{ fontWeight: 700 }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {meta.description}
      </Typography>

      <Divider />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        接続方式
      </Typography>
      <TextField
        select
        size="small"
        value={linkType}
        onChange={(e) => setLinkType(child.id, e.target.value as ConnectionType)}
      >
        {(Object.keys(CONNECTION_TYPE_META) as ConnectionType[]).map((k) => (
          <MenuItem key={k} value={k}>
            {CONNECTION_TYPE_META[k].label} — {CONNECTION_TYPE_META[k].description}
          </MenuItem>
        ))}
      </TextField>

      <Divider />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        リンク操作
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<LinkOffRoundedIcon />}
          onClick={() => disconnectLink(child.id)}
          disabled={isDisconnected}
        >
          切断
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<LinkRoundedIcon />}
          onClick={() => startConnectLink(child.id)}
          disabled={!isDisconnected}
        >
          接続
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<HealingRoundedIcon />}
          onClick={() => startConnectLink(child.id)}
          disabled={!isDisconnected}
        >
          再接続（疑似遅延）
        </Button>
      </Stack>
    </Stack>
  );
}
